/* Architectural blueprint-style diagrams.
   Thin linework, dashed schematic feel, mono labels, teal/gold accent.
   Pulses travel along link lines via SVG animateMotion.

   Exports:
     PillarMark          — per-offering schematic glyph (5 variations)
     WorkflowDiagram     — animated AI workflow schematic
     ArchitectureDiagram — animated web dev platform stack
     BlueprintCorner     — registration corner marks
*/

const INK    = "#1a1916";
const ACCENT = "#C9B188";
const FAINT  = "#9c9893";

/* ─── PillarMark ─────────────────────────────────────────────── */
export function PillarMark({ kind = "ai", size = 120, accent = ACCENT, ink = INK, t = 0 }) {
  const s = size;
  const c = s / 2;

  if (kind === "ai") {
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        <circle cx={c} cy={c} r={s * 0.05} fill={ink} />
        <circle cx={c} cy={c} r={s * 0.18} fill="none" stroke={ink} strokeWidth="0.6" />
        <circle cx={c} cy={c} r={s * 0.32} fill="none" stroke={ink} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.6" />
        <circle cx={c} cy={c} r={s * 0.46} fill="none" stroke={ink} strokeWidth="0.4" strokeDasharray="1 4" opacity="0.4" />
        {[0, 60, 120, 180, 240, 300].map((deg, i) => {
          const a = ((deg + t * 30) * Math.PI) / 180;
          const r = s * 0.32;
          return (
            <circle
              key={i}
              cx={c + Math.cos(a) * r}
              cy={c + Math.sin(a) * r}
              r={i === 0 ? 2.5 : 1.5}
              fill={i === 0 ? accent : ink}
              opacity={i === 0 ? 1 : 0.7}
            />
          );
        })}
        <line x1={c - s * 0.46} y1={c} x2={c - s * 0.18} y2={c} stroke={ink} strokeWidth="0.4" opacity="0.4" />
        <line x1={c + s * 0.18} y1={c} x2={c + s * 0.46} y2={c} stroke={ink} strokeWidth="0.4" opacity="0.4" />
      </svg>
    );
  }

  if (kind === "vending") {
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        <rect x={s * 0.22} y={s * 0.12} width={s * 0.56} height={s * 0.76} fill="none" stroke={ink} strokeWidth="0.7" />
        <rect x={s * 0.28} y={s * 0.18} width={s * 0.44} height={s * 0.34} fill="none" stroke={ink} strokeWidth="0.4" opacity="0.5" />
        {[0, 1, 2].map((i) => (
          <line key={`h-${i}`} x1={s * 0.28} y1={s * 0.26 + i * (s * 0.08)} x2={s * 0.72} y2={s * 0.26 + i * (s * 0.08)} stroke={ink} strokeWidth="0.3" opacity="0.4" />
        ))}
        {[0, 1, 2].map((row) =>
          [0, 1, 2, 3].map((col) => (
            <circle key={`s-${row}-${col}`} cx={s * 0.32 + col * (s * 0.12)} cy={s * 0.6 + row * (s * 0.08)} r="2" fill="none" stroke={ink} strokeWidth="0.4" opacity="0.5" />
          ))
        )}
        <rect x={s * 0.32} y={s * 0.84} width={s * 0.36} height={s * 0.02} fill={accent} opacity="0.8" />
        <line x1={s * 0.18} y1={s * 0.12} x2={s * 0.14} y2={s * 0.12} stroke={ink} strokeWidth="0.4" />
        <line x1={s * 0.18} y1={s * 0.88} x2={s * 0.14} y2={s * 0.88} stroke={ink} strokeWidth="0.4" />
        <line x1={s * 0.16} y1={s * 0.12} x2={s * 0.16} y2={s * 0.88} stroke={ink} strokeWidth="0.3" />
      </svg>
    );
  }

  if (kind === "media") {
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        <circle cx={c} cy={c} r={s * 0.06} fill={ink} />
        {[0.18, 0.28, 0.38, 0.48].map((rr, i) => (
          <path key={i} d={`M ${c - s * rr} ${c} A ${s * rr} ${s * rr} 0 0 1 ${c + s * rr} ${c}`}
            fill="none" stroke={i === 1 ? accent : ink} strokeWidth={i === 0 ? 0.7 : 0.5} opacity={1 - i * 0.18} />
        ))}
        <line x1={c} y1={s * 0.05} x2={c} y2={s * 0.95} stroke={ink} strokeWidth="0.3" strokeDasharray="2 3" opacity="0.35" />
        {[-3, -2, -1, 1, 2, 3].map((i) => (
          <line key={i} x1={c + i * s * 0.12} y1={c - 3} x2={c + i * s * 0.12} y2={c + 3} stroke={ink} strokeWidth="0.3" opacity="0.5" />
        ))}
        <line x1={s * 0.1} y1={c} x2={s * 0.9} y2={c} stroke={ink} strokeWidth="0.4" opacity="0.5" />
      </svg>
    );
  }

  if (kind === "marketing") {
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        {[0, 1, 2, 3].map((i) => (
          <line key={`gv-${i}`} x1={s * 0.18 + i * s * 0.21} y1={s * 0.18} x2={s * 0.18 + i * s * 0.21} y2={s * 0.82} stroke={ink} strokeWidth="0.3" opacity="0.2" />
        ))}
        {[0, 1, 2, 3].map((i) => (
          <line key={`gh-${i}`} x1={s * 0.18} y1={s * 0.18 + i * s * 0.21} x2={s * 0.82} y2={s * 0.18 + i * s * 0.21} stroke={ink} strokeWidth="0.3" opacity="0.2" />
        ))}
        <line x1={s * 0.18} y1={s * 0.82} x2={s * 0.82} y2={s * 0.82} stroke={ink} strokeWidth="0.5" />
        <line x1={s * 0.18} y1={s * 0.18} x2={s * 0.18} y2={s * 0.82} stroke={ink} strokeWidth="0.5" />
        <path d={`M ${s * 0.18} ${s * 0.72} Q ${s * 0.4} ${s * 0.58} ${s * 0.55} ${s * 0.42} T ${s * 0.82} ${s * 0.22}`} fill="none" stroke={ink} strokeWidth="0.7" />
        <path d={`M ${s * 0.18} ${s * 0.72} Q ${s * 0.4} ${s * 0.58} ${s * 0.55} ${s * 0.42} T ${s * 0.82} ${s * 0.22}`} fill="none" stroke={accent} strokeWidth="0.4" strokeDasharray="2 3" transform="translate(0 -3)" opacity="0.7" />
        {[[0.18, 0.72], [0.4, 0.55], [0.55, 0.42], [0.7, 0.32], [0.82, 0.22]].map(([x, y], i) => (
          <circle key={i} cx={s * x} cy={s * y} r={i === 4 ? 2.5 : 1.5} fill={i === 4 ? accent : ink} />
        ))}
        <circle cx={s * 0.82} cy={s * 0.22} r="6"  fill="none" stroke={accent} strokeWidth="0.4" />
        <circle cx={s * 0.82} cy={s * 0.22} r="9"  fill="none" stroke={accent} strokeWidth="0.3" opacity="0.5" />
      </svg>
    );
  }

  if (kind === "app") {
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        {[0, 1, 2].map((i) => {
          const yOff = i * s * 0.13;
          return (
            <g key={i}>
              <polygon
                points={`${s * 0.5},${s * 0.22 + yOff} ${s * 0.78},${s * 0.32 + yOff} ${s * 0.5},${s * 0.42 + yOff} ${s * 0.22},${s * 0.32 + yOff}`}
                fill="none" stroke={ink} strokeWidth="0.6" opacity={1 - i * 0.2}
              />
              {i === 0 && (
                <>
                  <line x1={s * 0.36} y1={s * 0.27 + yOff} x2={s * 0.64} y2={s * 0.37 + yOff} stroke={ink} strokeWidth="0.3" opacity="0.4" />
                  <line x1={s * 0.64} y1={s * 0.27 + yOff} x2={s * 0.36} y2={s * 0.37 + yOff} stroke={ink} strokeWidth="0.3" opacity="0.4" />
                </>
              )}
            </g>
          );
        })}
        <line x1={s * 0.5} y1={s * 0.22} x2={s * 0.5} y2={s * 0.68} stroke={ink} strokeWidth="0.3" strokeDasharray="2 2" opacity="0.4" />
        <circle cx={s * 0.5} cy={s * 0.18} r="2" fill={accent} />
        <line x1={s * 0.5} y1={s * 0.18} x2={s * 0.5} y2={s * 0.22} stroke={accent} strokeWidth="0.5" />
      </svg>
    );
  }

  return null;
}

/* ─── WorkflowDiagram ────────────────────────────────────────── */
/* Uses SVG animateMotion for smooth CSS-independent pulse travel */
export function WorkflowDiagram({ width = 720, height = 360, t = 0, accent = ACCENT, ink = INK }) {
  const w = width;
  const h = height;

  const nodes = [
    { x: 0.08, y: 0.5,  label: "INPUT",  kind: "in"   },
    { x: 0.28, y: 0.25, label: "PARSE",  kind: "step" },
    { x: 0.28, y: 0.75, label: "CTX",    kind: "step" },
    { x: 0.5,  y: 0.5,  label: "AGENT",  kind: "core" },
    { x: 0.72, y: 0.25, label: "ACT",    kind: "step" },
    { x: 0.72, y: 0.75, label: "LOG",    kind: "step" },
    { x: 0.92, y: 0.5,  label: "OUTPUT", kind: "out"  },
  ];

  const links = [[0,1],[0,2],[1,3],[2,3],[3,4],[3,5],[4,6],[5,6]];
  // Staggered durations for visual variety
  const linkDurs = [1.8, 2.2, 1.6, 2.0, 1.7, 2.3, 1.9, 2.1];
  const linkDelays = [0, 0.4, 0.7, 1.1, 0.2, 0.9, 0.5, 1.4];

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="wf-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke={ink} strokeWidth="0.3" opacity="0.08" />
        </pattern>
        {/* Glow filter for pulse dots */}
        <filter id="wf-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <rect width={w} height={h} fill="url(#wf-grid)" />
      <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill="none" stroke={ink} strokeWidth="0.5" opacity="0.3" />
      {/* Corner ticks */}
      {[[0,0],[w,0],[0,h],[w,h]].map(([x, y], i) => (
        <g key={i}>
          <line x1={x} y1={y} x2={x + (i % 2 === 0 ? 12 : -12)} y2={y} stroke={ink} strokeWidth="0.6" />
          <line x1={x} y1={y} x2={x} y2={y + (i < 2 ? 12 : -12)} stroke={ink} strokeWidth="0.6" />
        </g>
      ))}

      {/* Links with animateMotion pulses */}
      {links.map(([a, b], i) => {
        const A = nodes[a], B = nodes[b];
        const x1 = A.x * w, y1 = A.y * h, x2 = B.x * w, y2 = B.y * h;
        const pathId = `wf-path-${i}`;
        const d = `M ${x1} ${y1} L ${x2} ${y2}`;
        return (
          <g key={i}>
            <path id={pathId} d={d} fill="none" stroke={ink} strokeWidth="0.8" strokeDasharray="3 5" opacity="0.55" />
            {/* Animated pulse dot */}
            <circle r="2.5" fill={accent} filter="url(#wf-glow)" opacity="0.9">
              <animateMotion
                dur={`${linkDurs[i]}s`}
                begin={`${linkDelays[i]}s`}
                repeatCount="indefinite"
              >
                <mpath href={`#${pathId}`} />
              </animateMotion>
            </circle>
            {/* Second pulse, offset */}
            <circle r="1.5" fill={accent} opacity="0.55">
              <animateMotion
                dur={`${linkDurs[i] * 1.3}s`}
                begin={`${linkDelays[i] + linkDurs[i] * 0.5}s`}
                repeatCount="indefinite"
              >
                <mpath href={`#${pathId}`} />
              </animateMotion>
            </circle>
          </g>
        );
      })}

      {/* Nodes */}
      {nodes.map((n, i) => {
        const x = n.x * w, y = n.y * h;
        const isCore = n.kind === "core";
        const isIO   = n.kind === "in" || n.kind === "out";
        const nr     = isCore ? 22 : isIO ? 14 : 10;
        const nodeFill = "#0f1f1a";
        return (
          <g key={i}>
            {isCore && (
              <>
                <circle cx={x} cy={y} r={nr + 12} fill="none" stroke={ink} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.5" />
                <circle cx={x} cy={y} r={nr + 22} fill="none" stroke={ink} strokeWidth="0.3" strokeDasharray="1 4" opacity="0.3" />
                {/* Pulse ring on core */}
                <circle cx={x} cy={y} r={nr + 8} fill="none" stroke={accent} strokeWidth="0.8" opacity="0.0">
                  <animate attributeName="r" from={nr + 6} to={nr + 22} dur="2.5s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" from="0.55" to="0" dur="2.5s" repeatCount="indefinite"/>
                </circle>
              </>
            )}
            <circle cx={x} cy={y} r={nr} fill={nodeFill} stroke={ink} strokeWidth={isCore ? "0.9" : "0.6"} />
            {isCore && <circle cx={x} cy={y} r={nr * 0.4} fill={accent} opacity="0.9" />}
            {!isCore && <circle cx={x} cy={y} r="2.5" fill={ink} />}
            <text x={x} y={y + nr + 14} textAnchor="middle"
              style={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.18em", fill: ink, opacity: 0.7 }}>
              {n.label}
            </text>
          </g>
        );
      })}

      <text x="14" y="20" style={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.25em", fill: ink, opacity: 0.45 }}>
        FIG. 01 — AGENTIC WORKFLOW SCHEMATIC
      </text>
      <text x={w - 14} y="20" textAnchor="end" style={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.25em", fill: ink, opacity: 0.45 }}>
        DBG / 2026
      </text>
    </svg>
  );
}

/* ─── ArchitectureDiagram ────────────────────────────────────── */
export function ArchitectureDiagram({ width = 720, height = 360, t = 0, accent = ACCENT, ink = INK }) {
  const w = width;
  const h = height;
  const layers = [
    { name: "CLIENT",      sub: "iOS · Android · Web",          y: 0.18 },
    { name: "EDGE",        sub: "CDN · Cache · Auth",           y: 0.38 },
    { name: "APPLICATION", sub: "API · Services · Logic",       y: 0.58 },
    { name: "DATA",        sub: "Storage · Search · Analytics", y: 0.78 },
  ];

  // Vertical connector paths for animateMotion
  const vConnectors = [0, 1, 2].map((i) => {
    const y1 = layers[i].y * h + 10;
    const y2 = layers[i + 1].y * h - 20;
    return { left: `M ${w * 0.3} ${y1} L ${w * 0.3} ${y2}`, right: `M ${w * 0.7} ${y1} L ${w * 0.7} ${y2}` };
  });

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="ar-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke={ink} strokeWidth="0.3" opacity="0.08" />
        </pattern>
        <filter id="ar-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <rect width={w} height={h} fill="url(#ar-grid)" />
      <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill="none" stroke={ink} strokeWidth="0.5" opacity="0.3" />
      {/* Center spine */}
      <line x1={w * 0.5} y1={h * 0.1} x2={w * 0.5} y2={h * 0.88} stroke={ink} strokeWidth="0.3" strokeDasharray="2 3" opacity="0.3" />

      {layers.map((L, i) => {
        const y = L.y * h;
        return (
          <g key={i}>
            <line x1={w * 0.08} y1={y - 18} x2={w * 0.92} y2={y - 18} stroke={ink} strokeWidth="0.5" opacity="0.5" />
            {[0, 1, 2, 3, 4].map((j) => {
              const bw  = w * 0.13;
              const bx  = w * 0.1 + j * (bw + w * 0.025);
              const lit = ((t * 0.3 + i * 0.2 + j * 0.1) % 1) < 0.15;
              return (
                <rect key={j} x={bx} y={y - 14} width={bw} height="22"
                  fill={lit ? accent : "transparent"} fillOpacity={lit ? 0.35 : 0}
                  stroke={ink} strokeWidth="0.6" opacity={lit ? 1 : 0.75} />
              );
            })}
            <text x={w * 0.08} y={y - 26}
              style={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", letterSpacing: "0.25em", fill: ink, opacity: 0.75 }}>
              {String(i + 1).padStart(2, "0")} · {L.name}
            </text>
            <text x={w * 0.92} y={y - 26} textAnchor="end"
              style={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.2em", fill: ink, opacity: 0.45 }}>
              {L.sub}
            </text>
          </g>
        );
      })}

      {/* Vertical connectors with animateMotion pulses */}
      {vConnectors.map((paths, i) => (
        <g key={`v-${i}`}>
          {/* Left rail */}
          <path id={`ar-left-${i}`}  d={paths.left}  fill="none" stroke={ink} strokeWidth="0.5" strokeDasharray="3 4" opacity="0.4" />
          {/* Right rail */}
          <path id={`ar-right-${i}`} d={paths.right} fill="none" stroke={ink} strokeWidth="0.5" strokeDasharray="3 4" opacity="0.4" />

          {/* Left pulse */}
          <circle r="2.5" fill={accent} filter="url(#ar-glow)" opacity="0.9">
            <animateMotion dur={`${1.4 + i * 0.3}s`} begin={`${i * 0.45}s`} repeatCount="indefinite">
              <mpath href={`#ar-left-${i}`} />
            </animateMotion>
          </circle>
          {/* Right pulse */}
          <circle r="2.5" fill={accent} filter="url(#ar-glow)" opacity="0.9">
            <animateMotion dur={`${1.4 + i * 0.3}s`} begin={`${i * 0.45 + 0.7}s`} repeatCount="indefinite">
              <mpath href={`#ar-right-${i}`} />
            </animateMotion>
          </circle>
        </g>
      ))}

      <text x="14" y="20" style={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.25em", fill: ink, opacity: 0.45 }}>
        FIG. 02 — PLATFORM ARCHITECTURE
      </text>
      <text x={w - 14} y="20" textAnchor="end" style={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.25em", fill: ink, opacity: 0.45 }}>
        DBG / 2026
      </text>
    </svg>
  );
}

/* ─── BlueprintCorner ────────────────────────────────────────── */
export function BlueprintCorner({ size = 36, ink = INK, opacity = 0.6, position = "tl" }) {
  const flipX = position.includes("r");
  const flipY = position.includes("b");
  return (
    <svg
      width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ transform: `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})` }}
    >
      <line x1="0" y1="0" x2={size * 0.65} y2="0"        stroke={ink} strokeWidth="1"   opacity={opacity} />
      <line x1="0" y1="0" x2="0"           y2={size * 0.65} stroke={ink} strokeWidth="1"   opacity={opacity} />
      <circle cx={size * 0.2} cy={size * 0.2} r="2.5" fill="none" stroke={ink} strokeWidth="0.8" opacity={opacity} />
      {/* Small tick at the end of each arm */}
      <line x1={size * 0.65} y1="-3" x2={size * 0.65} y2="3" stroke={ink} strokeWidth="0.8" opacity={opacity * 0.6} />
      <line x1="-3" y1={size * 0.65} x2="3" y2={size * 0.65} stroke={ink} strokeWidth="0.8" opacity={opacity * 0.6} />
    </svg>
  );
}
